import moment from 'moment';
import tempMee from '../apis/tempMee';
import {
  GET_CREDENTIALS_V2_REQUEST,
  GET_CREDENTIALS_V2_SUCCESS,
  GET_CREDENTIALS_V2_FAILURE,
  CREDENTIAL_ACTION,
  SHOW_MESSAGE,
  EDUCATION_ACTION,
  AUTHORIZATION_ACTION,
} from './actionTypes';

import { UserType } from '../enums/UserType';
import { UserProfession } from '../enums/UserProfession';
import {
  getShouldUseProtectedS3Licenses,
  getShouldUsingCredentialServiceEndpoint,
} from '../growthbook';
import {
  getCredentialsV2,
  getProtectedProfLicenses,
  patchCredentials,
  patchCredentialsV2,
  getAuthorizations,
  createAuthorization as createAuthorizationApi,
  deleteAuthorization as deleteAuthorizationApi,
  updateCredentialArtifact,
  updateCredentialNumber,
  updateCredentialReference,
  updateCredentialNpi,
  createCredentialArtifact,
  createCredentialNumber,
  createCredentialReference,
  createCredentialNpi,
  updateAdminConfirmedName,
} from '../services/credentials';
import { patchUser } from '../services/users';
import { getCredentialType } from '../components/mainComponents/User/components/AuthorizationCredentialSection/credentialV2Utils';
import { showErrorMessage } from '../utils';

export const fetchLicensesV2 = (state) => async (dispatch) => {
  const params = {
    page: state?.page || 1,
    limit: 15,
    ...(state?.status && { status: state.status }),
    ...(state?.state && { state: state.state }),
    ...(state?.profession && { profession: state.profession }),
    ...(state?.credentialType && { credential_type: state.credentialType }),
    ...(state?.expiresSoon && { expiring_in_days: state.expiresSoon }),
  };

  dispatch({
    type: GET_CREDENTIALS_V2_REQUEST,
    payload: params,
  });

  try {
    const response = await tempMee.get('credential-service/authorizations/credentials', {
      params,
    });
    const credentials = (response.data?.credentials || []).map((cred, index) => ({
      ...cred,
      _rowKey: `${cred.id}_${cred.state}_${cred.profession}_${index}`,
    }));
    dispatch({
      type: GET_CREDENTIALS_V2_SUCCESS,
      payload: { ...response.data, credentials },
    });
  } catch (err) {
    dispatch({ type: GET_CREDENTIALS_V2_FAILURE, payload: err });
  }
};

/**
 * To get educations of professions
 *
 * @param {string[] | UserProfession[]} professions
 * @returns {(function(*): Promise<void>)|*}
 */
export const fetchRequestedEducations =
  (professions = [UserProfession.RDH, UserProfession.DA]) =>
  async (dispatch) => {
    dispatch({
      type: EDUCATION_ACTION.FETCH_REQUESTED_EDUCATIONS_REQUEST,
    });

    try {
      const responses = await Promise.all(
        professions.map((profession) =>
          tempMee.get('credential-service/credentials', {
            params: {
              profession,
              userType: UserType.HYG,
            },
          }),
        ),
      );

      const educations = responses.reduce((acc, response) => ({
        ...acc.data,
        ...response.data,
      }));

      // eslint-disable-next-line max-len
      dispatch({ type: EDUCATION_ACTION.FETCH_REQUESTED_EDUCATIONS_SUCCESS, payload: educations });
    } catch (err) {
      dispatch({ type: EDUCATION_ACTION.FETCH_REQUESTED_EDUCATIONS_FAILURE, payload: err });
    }
  };

export const getUserEducation =
  ({ userType, profession } = {}) =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: EDUCATION_ACTION.GET_USER_EDUCATION_REQUEST });

      const user = getState().users.currentUser;
      const type = userType || user?.user_type;
      const userProfession = profession || user?.licenseTypes?.[0] || 'RDH';

      const response = await tempMee.get(`credential-service/credentials`, {
        params: {
          profession: userProfession,
          userType: type,
        },
      });

      dispatch({
        type: EDUCATION_ACTION.GET_USER_EDUCATION_SUCCESS,
        payload: response.data[type === 'DNT' ? 'DNT' : userProfession],
      });
    } catch (err) {
      dispatch({ type: EDUCATION_ACTION.GET_USER_EDUCATION_FAILURE, payload: err });
    }
  };

export const updateUserEducation =
  // eslint-disable-next-line camelcase
  ({ years_of_experience, school, graduationDate, education, insurances }) =>
    async (dispatch, getState) => {
      try {
        dispatch({ type: EDUCATION_ACTION.UPDATE_USER_EDUCATION_REQUEST });

        const { users } = getState();
        const currentUser = users?.currentUser || {};
        const { id } = currentUser;

        const payload = {
          school,
          graduationDate,
          // eslint-disable-next-line camelcase
          ...(years_of_experience != null
            ? { professional_info: { exactYearsOfExperience: Number(years_of_experience) } }
            : {}),
        };

        if (getShouldUsingCredentialServiceEndpoint()) {
          await Promise.all([
            // patch yoe, school, graduation date
            patchUser(id, payload),
            // patch specialties & insurance via credentials service
            patchCredentialsV2(
              id,
              currentUser?.user_type,
              {
                ...currentUser?.professional_info?.education,
                ...education,
              },
              insurances,
            ),
          ]);

          dispatch({
            type: EDUCATION_ACTION.UPDATE_USER_EDUCATION_SUCCESS,
            payload: {
              ...currentUser,
              school,
              graduationDate,
              professional_info: {
                ...currentUser?.professional_info,
                ...payload?.professional_info,
                insurances: insurances || [],
                education: {
                  ...currentUser?.professional_info?.education,
                  ...education,
                },
              },
            },
          });
        } else {
          await patchUser(id, payload);
          const response = await patchCredentials(id, education, insurances);

          dispatch({
            type: EDUCATION_ACTION.UPDATE_USER_EDUCATION_SUCCESS,
            payload: {
              ...response.data,
              professional_info: {
                ...response.data?.professional_info,
                ...payload?.professional_info,
              },
            },
          });
        }
        dispatch({
          type: SHOW_MESSAGE,
          payload: {
            message: 'User education have been updated successfully.',
          },
        });
      } catch (err) {
        dispatch({ type: EDUCATION_ACTION.UPDATE_USER_EDUCATION_FAILURE, payload: err });
      }
    };

export const fetchStateCredentials = () => async (dispatch) => {
  dispatch({
    type: CREDENTIAL_ACTION.GET_ALL_STATE_CREDENTIALS_REQUEST,
  });

  try {
    const response = await tempMee.get('users/state-credentials');

    dispatch({ type: CREDENTIAL_ACTION.GET_ALL_STATE_CREDENTIALS_SUCCESS, payload: response.data });
  } catch (err) {
    dispatch({ type: CREDENTIAL_ACTION.GET_ALL_STATE_CREDENTIALS_FAILURE, payload: err });
  }
};

// eslint-disable-next-line consistent-return
export const updateLicenseCredential = (licenseId, license) => (dispatch) => {
  const validCredentials =
    license?.credentials?.map?.((credential) => {
      const { credential_type: credentialType, entry_field: entryField } = credential || {};

      if (credentialType === 'reference') {
        return {
          ...credential,
          entry_field: {
            // only send references key, anything else is not needed
            references: (entryField.references ?? []).filter(
              ({ officeName, contactEmail, employmentStart, employmentEnd }) =>
                officeName.trim().length > 0 &&
                contactEmail.trim().length > 0 &&
                !!employmentStart &&
                !!employmentEnd,
            ),
          },
        };
      }

      return credential;
    }) || [];

  // since field of Front Desk credential is different from other professions,
  // license profession/sub-profession need to be updated first before updating credentials
  // to avoid wrong input type fields error
  if (validCredentials) {
    // eslint-disable-next-line no-restricted-syntax
    for (const c of validCredentials) {
      const {
        credential_input_type: credentialInputType,
        credential_type: credentialType,
        entry_field: entryField,
        id,
      } = c || {};

      if (c) {
        const isReferenceCredentialValid =
          credentialType === 'reference' &&
          credentialInputType === 'entry_field' &&
          entryField?.references?.length > 0;
        const isLicenseCredentialValid =
          credentialType !== 'reference' &&
          credentialInputType === 'entry_field' &&
          entryField?.license;

        if (isReferenceCredentialValid || isLicenseCredentialValid) {
          if (!id) {
            dispatch(createCredentialEntryType(c, licenseId));
          } else {
            dispatch(updateCredentialEntryType(c, licenseId, id));
          }
        }
      }
    }
  }
};

export const createCredentialEntryType = (data, licId) => async (dispatch, getState) => {
  dispatch({
    type: CREDENTIAL_ACTION.POST_ENTRY_STATE_TYPE_CREDENTIALS_REQUEST,
  });
  try {
    const currUserId = getState().users.currentUser.id;

    await tempMee.post(`/admin/users/${currUserId}/licenses/${licId}/credentials`, data);
  } catch (err) {
    dispatch({ type: CREDENTIAL_ACTION.POST_ENTRY_STATE_TYPE_CREDENTIALS_FAILURE, payload: err });
  }
};

export const updateCredentialEntryType =
  (data, licId, credentialId) => async (dispatch, getState) => {
    dispatch({
      type: CREDENTIAL_ACTION.PATCH_ENTRY_STATE_TYPE_CREDENTIALS_REQUEST,
    });
    try {
      const currUserId = getState().users.currentUser.id;

      await tempMee.patch(
        `/admin/users/${currUserId}/licenses/${licId}/credentials/${credentialId}`,
        data
      );
    } catch (err) {
      dispatch({
        type: CREDENTIAL_ACTION.PATCH_ENTRY_STATE_TYPE_CREDENTIALS_FAILURE,
        payload: err,
      });
    }
  };

export const uploadCredential =
  // eslint-disable-next-line consistent-return
  (image, data, licId, credentialId) => async (dispatch, getState) => {
    try {
      dispatch({ type: CREDENTIAL_ACTION.UPLOAD_STATE_CREDENTIALS_REQUEST });

      const currUserId = getState().users.currentUser.id;
      const form = new FormData();
      form.append('image', image);

      if (data) {
        const credentialUploadData = await tempMee.post(
          `/admin/users/${currUserId}/licenses/${licId}/credentials-upload`,
          data
        );

        // eslint-disable-next-line no-param-reassign
        credentialId = credentialUploadData.data.id;
      }

      const response = await tempMee.patch(
        `/admin/users/${currUserId}/licenses/${licId}/credentials-upload/${credentialId}`,
        form,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      let newCredential = response.data;
      if (getShouldUseProtectedS3Licenses()) {
        newCredential = await dispatch(getProtectedLicenseCredential(licId, newCredential.id));
      }
      dispatch({
        type: SHOW_MESSAGE,
        payload: {
          message: 'Credential is uploaded',
        },
      });

      return newCredential;
    } catch (error) {
      dispatch({ type: CREDENTIAL_ACTION.UPLOAD_STATE_CREDENTIALS_FAILURE, payload: error });
    }
  };

export const deleteCredentialUpload = (licId, credentialId) => async (dispatch, getState) => {
  try {
    dispatch({ type: CREDENTIAL_ACTION.DELETE_CREDENTIALS_UPLOAD_REQUEST });

    const currUserId = getState().users.currentUser.id;

    await tempMee.delete(
      `/admin/users/${currUserId}/licenses/${licId}/credentials-upload/${credentialId}`
    );
    dispatch({
      type: SHOW_MESSAGE,
      payload: {
        message: 'Credential is deleted',
      },
    });
  } catch (error) {
    dispatch({ type: CREDENTIAL_ACTION.DELETE_CREDENTIALS_UPLOAD_FAILURE, payload: error });
  }
};

export const deleteCredentialFile = (licId, credentialId, fileId) => async (dispatch, getState) => {
  try {
    dispatch({ type: CREDENTIAL_ACTION.DELETE_CREDENTIALS_UPLOAD_REQUEST });

    const currUserId = getState().users.currentUser.id;

    await tempMee.delete(
      `/admin/users/${currUserId}/licenses/${licId}/credentials-upload/${credentialId}/${fileId}`
    );
    dispatch({
      type: SHOW_MESSAGE,
      payload: {
        message: 'Image is deleted',
      },
    });
  } catch (error) {
    dispatch({ type: CREDENTIAL_ACTION.DELETE_CREDENTIALS_UPLOAD_FAILURE, payload: error });
  }
};

export const fetchOfficeCredentials = (userId) => async (dispatch) => {
  try {
    dispatch({ type: CREDENTIAL_ACTION.GET_OFFICE_CREDENTIALS_REQUEST });

    const response = await getCredentialsV2(userId, UserType.DNT);

    dispatch({
      type: CREDENTIAL_ACTION.GET_OFFICE_CREDENTIALS_SUCCESS,
      payload: {
        softwares: response.data?.softwares || [],
        insurances: response.data?.insurances || [],
      },
    });
  } catch (err) {
    dispatch({ type: CREDENTIAL_ACTION.GET_OFFICE_CREDENTIALS_FAILURE, payload: err });
  }
};

export const fetchUserCredentials = () => async (dispatch, getState) => {
  if (!getShouldUsingCredentialServiceEndpoint()) {
    return;
  }

  try {
    const { users } = getState();
    const currentUser = users?.currentUser || {};

    dispatch({ type: CREDENTIAL_ACTION.GET_USER_CREDENTIALS_REQUEST });

    const response = await getCredentialsV2(currentUser?.id, currentUser?.user_type);

    // eslint-disable-next-line camelcase
    const professional_info = {
      ...currentUser.professional_info,
      insurances: response.data?.insurances || [],
      softwares: response.data?.softwares || [],
      education: response?.data?.education,
    };

    dispatch({
      type: CREDENTIAL_ACTION.GET_USER_CREDENTIALS_SUCCESS,
      payload: {
        // eslint-disable-next-line camelcase
        professional_info,
      },
    });
  } catch (err) {
    dispatch({
      type: CREDENTIAL_ACTION.GET_USER_CREDENTIALS_FAILURE,
      payload: err,
    });
  }
};

export const getProtectedLicenseCredential =
  (licenseId, credentialId) => async (dispatch, getState) => {
    const currUserId = getState().users.currentUser.id;

    const response = await getProtectedProfLicenses(currUserId);
    const licenses = response.data || [];
    const license = licenses.find((item) => item.id === licenseId);

    const credential = license?.credentials?.find?.((item) => item.id === credentialId);
    return credential;
  };

// Download a remote (presigned) doc into a local blob so it can be re-uploaded on save.
const downloadToLocalFile = async (url, fileId) => {
  const response = await fetch(url);
  const blob = await response.blob();
  const extension = blob.type?.split('/')?.[1] || 'png';
  const fileName = fileId || `file-${Date.now()}.${extension}`;
  const localFile = new File([blob], fileName, { type: blob.type });
  return {
    _local: true,
    _file: localFile,
    file_id: fileName,
    url: URL.createObjectURL(localFile),
  };
};

// confirmed_name.document_urls (presigned) -> local blobs the uploader can display + re-send.
const prefetchConfirmedNameDocs = async (confirmedName) => {
  const urls = confirmedName?.document_urls || [];
  if (!urls.length) return confirmedName;

  const files = await Promise.all(
    urls.map(async (url, index) => {
      try {
        return await downloadToLocalFile(url, `confirmed-name-${index}`);
      } catch {
        // presign fetch failed: keep it viewable, just not re-uploadable
        return { url, file_id: `confirmed-name-${index}` };
      }
    }),
  );

  return { ...confirmedName, _files: files };
};

const prefetchArtifactFiles = async (authorizations) => {
  const promises = authorizations.map(async (auth) => {
    const creds = auth?.credentials || [];
    const updatedCreds = await Promise.all(
      creds.map(async (cred) => {
        if (cred?.type !== 'artifact') return cred;

        const files = cred?.data?.artifact?.files || [];
        if (!files.length) return cred;

        const cachedFiles = await Promise.all(
          files.map(async (file) => {
            // eslint-disable-next-line no-underscore-dangle
            if (file._local) return file;
            try {
              const response = await fetch(file.url);
              const blob = await response.blob();
              const extension = blob.type?.split('/')?.[1] || 'png';
              const fileName = file.file_id || `file-${Date.now()}.${extension}`;
              const localFile = new File([blob], fileName, { type: blob.type });
              return {
                ...file,
                _local: true,
                _file: localFile,
                url: URL.createObjectURL(localFile),
              };
            } catch {
              return file;
            }
          }),
        );

        return {
          ...cred,
          data: {
            ...cred.data,
            artifact: { ...cred.data.artifact, files: cachedFiles },
          },
        };
      }),
    );

    const confirmedName = await prefetchConfirmedNameDocs(auth?.confirmed_name);

    return { ...auth, credentials: updatedCreds, confirmed_name: confirmedName };
  });

  return Promise.all(promises);
};

export const fetchAuthorizations = (professionalId) => async (dispatch) => {
  try {
    dispatch({ type: AUTHORIZATION_ACTION.FETCH_AUTHORIZATIONS_REQUEST });

    const response = await getAuthorizations(professionalId);
    const authorizations = response.data?.authorizations || [];
    const withCachedFiles = await prefetchArtifactFiles(authorizations);

    dispatch({
      type: AUTHORIZATION_ACTION.FETCH_AUTHORIZATIONS_SUCCESS,
      payload: withCachedFiles,
    });
  } catch (err) {
    dispatch({
      type: AUTHORIZATION_ACTION.FETCH_AUTHORIZATIONS_FAILURE,
      payload: err,
    });
  }
};

export const createAuthorizationCredential =
  (professionalId, { state, profession }) =>
  async (dispatch) => {
    try {
      dispatch({ type: AUTHORIZATION_ACTION.CREATE_AUTHORIZATION_REQUEST });

      await createAuthorizationApi(professionalId, { state, profession });

      dispatch({ type: AUTHORIZATION_ACTION.CREATE_AUTHORIZATION_SUCCESS });
      dispatch({
        type: SHOW_MESSAGE,
        payload: { message: 'Professional Credential successfully.' },
      });

      // refetch list after creation
      dispatch(fetchAuthorizations(professionalId));
    } catch (err) {
      dispatch({
        type: AUTHORIZATION_ACTION.CREATE_AUTHORIZATION_FAILURE,
        payload: err,
      });
    }
  };

export const deleteAuthorization =
  (professionalId, authorizationId) =>
  async (dispatch) => {
    try {
      dispatch({ type: AUTHORIZATION_ACTION.DELETE_AUTHORIZATION_REQUEST });

      await deleteAuthorizationApi(professionalId, authorizationId);

      dispatch({ type: AUTHORIZATION_ACTION.DELETE_AUTHORIZATION_SUCCESS });
      dispatch({
        type: SHOW_MESSAGE,
        payload: { message: 'Professional Credential deleted successfully.' },
      });

      dispatch(fetchAuthorizations(professionalId));
    } catch (err) {
      dispatch({
        type: AUTHORIZATION_ACTION.DELETE_AUTHORIZATION_FAILURE,
        payload: err,
      });
    }
  };

const formatDate = (d) => {
  if (!d) return null;
  const m = moment.utc(d);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

/* eslint-disable camelcase, no-underscore-dangle */
export const saveCredentials =
  (professionalId, authorization) => async (dispatch) => {
    try {
      const {
        credentials,
        id: authorizationId,
        state: authState,
        confirmed_name: confirmedName,
        _confirmedNameChanged: confirmedNameChanged,
      } = authorization;
      dispatch({
        type: AUTHORIZATION_ACTION.SAVE_CREDENTIALS_REQUEST,
      });

      const promises = credentials.map((cred) => {
        const { type, id, credential, status: credStatus } = cred;

        if (type === 'artifact') {
          const artifactData = cred?.data?.artifact || {};
          const artifactPayload = {
            credential_type: getCredentialType(credential),
            issue_date: formatDate(cred.issued_at),
            expire_date: formatDate(cred.expired_at),
            status: credStatus,
            files: artifactData.files || [],
          };
          if (!artifactPayload.issue_date || !artifactPayload.files?.length) {
            return Promise.resolve();
          }

          return id
            ? updateCredentialArtifact(professionalId, id, artifactPayload)
            : createCredentialArtifact(professionalId, authorizationId, artifactPayload);
        }

        if (type === 'number') {
          const numberData = cred?.data?.number || {};
          const numberPayload = {
            credential_type: getCredentialType(credential),
            number: numberData.credential_number,
            issue_date: formatDate(cred.issued_at),
            expire_date: formatDate(cred.expired_at),
            status: credStatus,
          };
          if (!numberPayload.number || !numberPayload.issue_date || !numberPayload.expire_date) {
            return Promise.resolve();
          }

          return id
            ? updateCredentialNumber(professionalId, id, numberPayload)
            : createCredentialNumber(professionalId, authorizationId, numberPayload);
        }

        if (type === 'reference') {
          const refData = cred?.data?.reference || {};
          const refPayload = {
            credential_type: 'references',
            office_name: refData.office_name,
            contact_email: refData.contact_email,
            start_date: formatDate(refData.start_date),
            end_date: formatDate(refData.end_date),
            status: credStatus,
          };
          if (
            !refPayload.office_name ||
            !refPayload.contact_email ||
            !refPayload.start_date ||
            !refPayload.end_date
          ) {
            return Promise.resolve();
          }
          return id
            ? updateCredentialReference(professionalId, id, refPayload)
            : createCredentialReference(professionalId, authorizationId, refPayload);
        }

        if (type === 'npi') {
          const npiData = cred?.data?.npi || {};
          const npiPayload = {
            credential_type: 'npi_number',
            npi_number: npiData.npi_number,
            status: credStatus,
          };
          if (!npiPayload.npi_number) {
            return Promise.resolve();
          }

          return id
            ? updateCredentialNpi(professionalId, id, npiPayload)
            : createCredentialNpi(professionalId, authorizationId, npiPayload);
        }

        return Promise.resolve();
      });

      const tasks = promises.map((promise, idx) => ({
        promise,
        name: credentials[idx]?.credential_name || credentials[idx]?.credential || 'Unknown',
      }));

      // CS-confirmed name is per-authorization/state, saved via its own admin endpoint.
      // Always re-send the full doc set: the handler clears docs when no files part is
      // present, so omitting them (even on names-only edits) would wipe existing uploads.
      if (confirmedNameChanged) {
        tasks.push({
          name: 'Confirmed Name',
          promise: updateAdminConfirmedName(professionalId, {
            state: authState,
            first_name: confirmedName?.first_name ?? '',
            last_name: confirmedName?.last_name ?? '',
            files: confirmedName?._files,
          }),
        });
      }

      const namedPromises = tasks.map(({ promise, name }) =>
        Promise.resolve(promise).then(
          (val) => ({ status: 'fulfilled', value: val, name }),
          (err) => ({ status: 'rejected', reason: err, name }),
        ),
      );

      const results = await Promise.all(namedPromises);
      const failures = results.filter((r) => r.status === 'rejected');

      if (failures.length > 0) {
        const errorMessages = failures.map((f) => {
          const errMsg = f.reason?.response?.data?.error || f.reason?.response?.data?.message || f.reason?.message || 'Unknown error';
          return `${f.name}: ${errMsg}`;
        });

        // dispatch({
        //   type: AUTHORIZATION_ACTION.SAVE_CREDENTIALS_FAILURE,
        //   payload: failures[0].reason,
        // });

        showErrorMessage({
          message: `${failures.length} credential(s) failed to update - ${errorMessages.join('; ')}`,
        });
      }

      if (failures.length < results.length) {
        dispatch({
          type: AUTHORIZATION_ACTION.SAVE_CREDENTIALS_SUCCESS,
        });

        if (failures.length === 0) {
          dispatch({
            type: SHOW_MESSAGE,
            payload: {
              message: 'Credentials updated successfully.',
            },
          });
        }
      }
    } finally {
      setTimeout(() => {
        dispatch(fetchAuthorizations(professionalId));
      }, 1000);
    }
  };
/* eslint-enable camelcase, no-underscore-dangle */
